<template>
  <div class="w-full">
    <Head title="Wizyty i płatności" />
    <h1 class="mb-8 text-3xl font-bold">
      Wizyty i płatności
    </h1>

    <div class="mb-6 flex flex-wrap items-end gap-x-4 gap-y-2">
      <div>
        <label
          for="visit-filter-search"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Wizyta
        </label>
        <input
          id="visit-filter-search"
          v-model="form.search"
          type="search"
          autocomplete="off"
          placeholder="UUID, PayU, Medfile, pacjent…"
          class="form-input w-[260px] py-1.5 text-sm"
        >
      </div>
      <div>
        <label
          for="visit-filter-specialist"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Specjalista
        </label>
        <input
          id="visit-filter-specialist"
          v-model="form.specialist"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <div>
        <label
          for="visit-filter-payment-status"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Status płatności
        </label>
        <select
          id="visit-filter-payment-status"
          v-model="form.payment_status"
          class="form-select w-[180px] py-1.5 text-sm"
        >
          <option value="">
            Wszystkie
          </option>
          <option value="pending">
            Oczekuje
          </option>
          <option value="completed">
            Opłacona
          </option>
          <option value="canceled">
            Anulowana
          </option>
          <option value="not_required">
            Bez przedpłaty
          </option>
        </select>
      </div>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="text-sm text-gray-500 hover:text-gray-800"
        @click="resetFilters"
      >
        Wyczyść
      </button>
    </div>

    <div class="overflow-hidden rounded-sm bg-white shadow-sm">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="px-6 py-4">
              Pacjent
            </th>
            <th class="px-6 py-4">
              Specjalista
            </th>
            <th class="px-6 py-4">
              Usługa
            </th>
            <th class="px-6 py-4">
              Termin
            </th>
            <th class="px-6 py-4">
              Płatność
            </th>
            <th class="px-6 py-4">
              Status
            </th>
            <th class="px-6 py-4">
              Utworzono
            </th>
          </tr>
        </thead>
        <tbody v-if="visits.data.length">
          <tr
            v-for="visit in visits.data"
            :key="visit.id"
            class="hover:bg-gray-50"
          >
            <td class="border-t px-6 py-3">
              <div>
                {{ visit.patient_name }} {{ visit.patient_lastname }}
              </div>
              <div class="text-sm text-gray-500">
                {{ visit.email }}
              </div>
              <div class="text-sm text-gray-500">
                {{ visit.phone }}
              </div>
            </td>
            <td class="border-t px-6 py-3">
              {{ visit.specialist_name || '—' }}
            </td>
            <td class="border-t px-6 py-3">
              <span class="max-w-xs whitespace-normal block">
                {{ visit.service_name || '—' }}
              </span>
            </td>
            <td class="border-t px-6 py-3 text-sm text-gray-500">
              {{ visit.chosen_date || '—' }}
            </td>
            <td class="border-t px-6 py-3">
              <div>{{ formatAmount(visit.amount_pln, visit.currency) }}</div>
              <div class="mt-1">
                <span :class="pillClass(paymentTone(visit.payment_status))">
                  {{ paymentLabel(visit.payment_status) }}
                </span>
              </div>
              <div
                v-if="visit.payu_payment_id"
                class="mt-1 max-w-[14rem] text-xs text-gray-500"
                :title="visit.payu_payment_id"
              >
                <span class="text-gray-400">ID Płatności:</span>
                <span class="block truncate font-mono text-gray-600">{{ visit.payu_payment_id }}</span>
              </div>
              <div
                v-if="visit.payu_order_id"
                class="mt-1 max-w-[14rem] text-xs text-gray-500"
                :title="visit.payu_order_id"
              >
                <span class="text-gray-400">ID Zamówienia PayU:</span>
                <span class="block truncate font-mono text-gray-600">{{ visit.payu_order_id }}</span>
              </div>
              <a
                v-if="showPaymentLink(visit)"
                :href="visit.payu_redirect_uri"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-2 inline-flex text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                Link do płatności →
              </a>
            </td>
            <td class="border-t px-6 py-3">
              <span :class="pillClass(statusTone(visit.status))">
                {{ statusLabel(visit.status) }}
              </span>
              <div
                v-if="visit.medfile_visit_uuid"
                class="mt-1 max-w-[14rem] text-xs text-gray-500"
                :title="visit.medfile_visit_uuid"
              >
                <span class="text-gray-400">ID wizyty Medfile:</span>
                <span class="block truncate font-mono text-gray-600">{{ visit.medfile_visit_uuid }}</span>
              </div>
            </td>
            <td class="border-t px-6 py-3 text-sm text-gray-500">
              {{ formatDate(visit.created_at) }}
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="7" class="border-t px-6 py-6 text-center text-gray-500">
              {{ emptyMessage }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Pagination :meta="visits.meta" class="mt-6" />
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3';
import Pagination from '@/Shared/Pagination.vue';
import pickBy from 'lodash/pickBy';
import throttle from 'lodash/throttle';
import { createLayoutHelper } from '@/utils/layoutHelper';

export default {
  components: {
    Head,
    Pagination,
  },
  layout: createLayoutHelper,
  props: {
    visits: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: {
        search: this.filters.search || '',
        specialist: this.filters.specialist || '',
        payment_status: this.filters.payment_status || '',
      },
    };
  },
  computed: {
    hasActiveFilters() {
      return !!(
        this.form.search?.trim()
        || this.form.specialist?.trim()
        || this.form.payment_status
      );
    },
    emptyMessage() {
      return this.hasActiveFilters
        ? 'Brak wizyt pasujących do filtrów.'
        : 'Brak wizyt.';
    },
  },
  watch: {
    form: {
      handler: throttle(function handler() {
        const query = pickBy({
          search: this.form.search?.trim(),
          specialist: this.form.specialist?.trim(),
          payment_status: this.form.payment_status || undefined,
        });
        this.$inertia.get(
          this.$routes.admin_visits(Object.keys(query).length ? query : {}),
          {},
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            only: ['visits'],
          },
        );
      }, 150),
      deep: true,
    },
  },
  methods: {
    resetFilters() {
      this.form.search = '';
      this.form.specialist = '';
      this.form.payment_status = '';
    },
    showPaymentLink(visit) {
      return visit.payment_status === 'pending' && !!visit.payu_redirect_uri;
    },
    formatDate(value) {
      if (!value) return '—';
      try {
        return new Date(value).toLocaleString('pl-PL', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return value;
      }
    },
    formatAmount(amount, currency) {
      if (amount == null) return '—';
      return `${Number(amount).toFixed(2)} ${currency || 'PLN'}`;
    },
    paymentLabel(status) {
      return {
        pending: 'Oczekuje',
        completed: 'Opłacona',
        canceled: 'Anulowana',
        not_required: 'Bez przedpłaty',
      }[status] || status;
    },
    statusLabel(status) {
      return {
        pending_payment: 'Oczekuje na płatność',
        paid: 'Opłacona',
        registered: 'Zarejestrowana',
        payment_canceled: 'Płatność anulowana',
        failed: 'Błąd rejestracji',
      }[status] || status;
    },
    paymentTone(status) {
      return {
        pending: 'amber',
        completed: 'green',
        canceled: 'red',
        not_required: 'sky',
      }[status] || 'gray';
    },
    statusTone(status) {
      return {
        pending_payment: 'amber',
        paid: 'sky',
        registered: 'green',
        payment_canceled: 'red',
        failed: 'red',
      }[status] || 'gray';
    },
    pillClass(tone) {
      const tones = {
        green: 'bg-green-100 text-green-800',
        amber: 'bg-amber-100 text-amber-800',
        red: 'bg-red-100 text-red-800',
        sky: 'bg-sky-100 text-sky-800',
        gray: 'bg-gray-200 text-gray-800',
      };
      return `inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${tones[tone] || tones.gray}`;
    },
  },
};
</script>
